/**
 * The alert vocabulary, and the translation from wizard form state into the
 * stored `alert_spec` document.
 *
 * Kept pure and separate from the step components for the same reason the API
 * keeps `services/alerts/comparison.py` out of its route module: the refusals
 * below need to be testable one at a time, without mounting a wizard.
 *
 * **This module was rewritten for PRD-1321, against a model that changed
 * underneath it.** Until PRD-1319 an alert had *two* definitions — a query the
 * author previewed, and a comparison beside it that named physical tables and
 * columns and ignored the query entirely. The comparison half of this file
 * existed to collect those identifiers: which table to read, which column
 * identifies the subject, which column to measure, how far back to look, which
 * rows to filter to. Every one of them was typed, and none of them was checked
 * against the query the author had just run.
 *
 * Under the model this file now serves, the query holds all of that. What
 * remains is what to do with the number the query returned, and every column
 * named below is a **result column** — one the author was shown and picked from
 * a list, never one they had to know the name of.
 *
 * **Everything here mirrors the API rather than inventing a parallel rule.**
 * `ResultComparison.__post_init__` is the authority; `validateAlertSpecInputs`
 * restates its refusals so they arrive as inline messages rather than as a 422
 * after the whole form is filled in. Where the two could drift, a test asserts
 * they agree — see `alertSpec.test.js`.
 */

/**
 * The document version this wizard writes.
 *
 * `stored.py` discriminates on the presence of `sql` rather than on this number,
 * deliberately — every shape carries a `version` and they all count from 1, so
 * the numbers describe different things. It is written anyway because the stored
 * document is the record of what the author configured, and a reader should not
 * have to infer the shape it is looking at.
 *
 * **Version 3 stores the query itself, not an intermediate representation of it
 * (PRD-1405).** Version 2 stored an IR the vocabulary could express, and the
 * vocabulary expressed 2 of 15 ordinary dashboard questions — a table alias, a
 * cast, a `COALESCE` across two columns were each a refusal. What is stored is
 * now the generated SQL, its window held as placeholders so it keeps meaning a
 * moving period, and the columns the query was seen to return.
 */
export const ALERT_DOC_VERSION = 3;

/**
 * What the comparison does with what the query returned.
 *
 * **Three shapes, not five.** The old model needed `count_in_window`,
 * `sum_in_window`, `date_vs_relation`, `missing_relation` and `consecutive`
 * because each described a different way of *getting to* a number. With the
 * query producing the number, a join and an anti-join are things the query
 * does — so what is left is the three ways of reading the result.
 *
 * `uses` records which controls are meaningful, so the step can hide the rest.
 * That is not cosmetic: the API **refuses** a field the shape does not read
 * rather than ignoring it, on the ground that a field silently dropped is worse
 * than a refusal because the author believes it is in force. A control the form
 * offered and the API then refused would be the same trap one layer up.
 */
export const COMPARISON_SHAPES = [
  {
    id: 'threshold',
    label: 'Compare a value against a threshold',
    description:
      'The query returns one row per subject. Each subject\'s value is compared '
      + 'against the threshold, and the ones that breach it are the matches.',
    uses: { measureColumn: true, comparator: true, threshold: true },
  },
  {
    id: 'any_rows',
    label: 'Match every row the query returned',
    description:
      'The query found records that should not exist, so there is nothing to '
      + 'compare — every row it returns is a match.',
    uses: {},
  },
  {
    id: 'run_length',
    label: 'Breached for several periods in a row',
    description:
      'A subject matches only once it breaches the threshold on that many '
      + 'consecutive periods. Scattered breaches do not count.',
    uses: {
      measureColumn: true,
      comparator: true,
      threshold: true,
      periodColumn: true,
      periodUnit: true,
      runLength: true,
    },
  },
];

/** Whether a shape uses a given control at all. */
export const shapeUses = (shape, field) => {
  const s = COMPARISON_SHAPES.find((c) => c.id === shape);
  return s ? (s.uses[field] ?? false) : false;
};

/**
 * `PeriodUnit`, and `shared.alert_ir.PERIOD_UNITS`.
 *
 * How far apart two adjacent buckets have to be before they stop being
 * consecutive. Required for a run — without it "consecutive" can only mean
 * consecutive *rows*, and the period that should break a run is precisely the
 * one the result omits.
 */
export const PERIOD_UNITS = [
  { id: 'day', label: 'Days' },
  { id: 'week', label: 'Weeks' },
  { id: 'month', label: 'Months' },
];

/**
 * Where the threshold comes from.
 *
 * A **column** means each subject is compared against its own value, which the
 * previous model could not express at all. The API enforces exactly one of the
 * two, so this is a choice rather than two independent inputs that could both
 * be filled in.
 */
export const THRESHOLD_KINDS = [
  { id: 'value', label: 'A fixed number' },
  { id: 'column', label: 'A value from the result' },
];

/**
 * `Comparator`, with the inclusivity spelled out.
 *
 * The operator is stored rather than assumed precisely so the UI can state the
 * rule in force, which is what this ticket's AC6 asks for. `>=` and `<=`
 * include a value sitting exactly on the threshold; `>` and `<` exclude it.
 * That boundary is the difference between an alert firing and staying quiet.
 */
export const COMPARATORS = [
  { id: '>=', label: 'at or above (≥)', inclusive: true },
  { id: '>', label: 'above (>)', inclusive: false },
  { id: '<=', label: 'at or below (≤)', inclusive: true },
  { id: '<', label: 'below (<)', inclusive: false },
];

/** `SEVERITIES` in `services/alerts/authoring.py`. */
export const SEVERITIES = [
  { id: 'info', label: 'Info' },
  { id: 'warning', label: 'Warning' },
  { id: 'critical', label: 'Critical' },
];

/**
 * `CHANNELS` and `DELIVERABLE_CHANNELS`.
 *
 * SMS is a channel the system understands and cannot yet send on. The API
 * refuses it as a capability gap rather than a bad value, and this list mirrors
 * that distinction so the disabled reason matches the refusal the author would
 * otherwise hit on create.
 *
 * Making SMS selectable is the last step of PRD-1257, not a change here.
 */
export const CHANNELS = [
  { id: 'email', label: 'Email', deliverable: true },
  {
    id: 'sms',
    label: 'SMS',
    deliverable: false,
    reason:
      'SMS cannot be delivered yet, so an alert configured for SMS would evaluate '
      + 'on schedule and notify nobody. Use email.',
  },
];

/**
 * Comparison fields that change WHAT the alert measures rather than where it
 * fires. Mirrors `SURGICAL_COMPARISON_FIELDS` on the far side by complement:
 * anything not on the instant path there belongs here.
 */
export const MEASUREMENT_ALERT_FIELDS = ['shape', 'subjectColumn', 'measureColumn', 'periodColumn'];

/**
 * Did this edit change what the alert measures, or only where it fires?
 *
 * **The console's copy of a rule the server owns, and deliberately the blunter
 * one.** `services/alerts/editing.py::classify_edit` is the authority — it
 * decides whether the alert is disarmed and whether the stored query is written
 * back. This exists only to decide whether the author must re-verify a preview
 * before saving, so where the two could disagree, this one says *it changed*.
 *
 * The query is compared as text, which is blunter still now that the query *is*
 * text: two generations of the same question differ by whitespace and this calls
 * them different — a false positive, costing a re-verification nobody needed.
 * The opposite mistake lets an author save a changed measurement without looking
 * at what it now matches, which is the whole point of the gate. Normalising the
 * SQL to compare it would mean a parser in the console, which is the thing this
 * layer deliberately does not have.
 *
 * **The window is compared too, and leaving it out was a real hole.** The stored
 * SQL holds its period as `%(window_start)s` / `%(window_end)s` — the day count
 * lives only in `window`. `rewrite_window` emits the same two-ended predicate
 * whatever the offset, so "the last 7 days" and "the last 30 days" produce
 * **byte-identical SQL**; verified by running the two through it. Comparing SQL
 * alone, an author could widen an alert from a week to a month, see no gate, and
 * save it — and because the signature matched, an acknowledgement given for the
 * 7-day preview stayed valid for the 30-day one. That is exactly the substitution
 * the signature exists to prevent. Under version 2 the offset was inside the IR,
 * so serialising it caught this. Raised in review of this branch.
 *
 * Missing state resolves the same way: an edit that cannot be compared has not
 * been shown to have left the measurement alone.
 */
export const isMeasurementChange = (before, after) => {
  if (!before || !after) return true;
  try {
    if (JSON.stringify(before.sql ?? null) !== JSON.stringify(after.sql ?? null)) return true;
    if (JSON.stringify(before.window ?? null) !== JSON.stringify(after.window ?? null)) return true;
  } catch {
    return true;
  }
  return MEASUREMENT_ALERT_FIELDS.some((field) => (before[field] ?? null) !== (after[field] ?? null));
};

/**
 * A stable fingerprint of what an alert measures, for pinning an acknowledgement
 * to the thing that was acknowledged (PRD-1382).
 *
 * Returns null when it cannot be computed, which reads downstream as "not
 * acknowledged" — the author is asked again rather than waved through on a
 * signature nobody could produce.
 */
export const measurementSignatureOf = (alert) => {
  if (!alert) return null;
  try {
    return JSON.stringify([
      alert.sql ?? null,
      // See `isMeasurementChange`: the period is not in the query text, so a
      // signature over the SQL alone carries an acknowledgement across a change
      // from one window to another.
      alert.window ?? null,
      ...MEASUREMENT_ALERT_FIELDS.map((f) => alert[f] ?? null),
    ]);
  } catch {
    return null;
  }
};

/** The default that applies at evaluation when the author sets no cap (PRD-1288). */
export const VOLUME_GUARD_DEFAULT_MAX_MATCHES = 25;

/**
 * The cap actually in force for a configured value.
 *
 * Mirrors `volume_guard.default_max_matches` on the far side, including the part
 * that is easy to get wrong: **a configured 0 is honoured, not replaced.** Zero
 * means "hold every run", which is a deliberate choice, and the obvious
 * `configured || DEFAULT` would silently overwrite it with 25 — turning the
 * strictest setting available into a permissive one. Only an unset value
 * defaults.
 */
export const effectiveMaxMatches = (configured) => {
  if (configured === '' || configured === null || configured === undefined) {
    return VOLUME_GUARD_DEFAULT_MAX_MATCHES;
  }
  const parsed = Number(configured);
  return Number.isFinite(parsed) ? parsed : VOLUME_GUARD_DEFAULT_MAX_MATCHES;
};

/**
 * The refusal classes the author-time endpoint returns, as messages an author
 * can act on.
 *
 * **Branching on the class is the whole point of it being classed.** PRD-1355
 * relays `detail.reason` from the far side with its class intact, specifically
 * so this layer does not have to match on message text. A validation dump —
 * which is what the author saw before the classes existed — tells someone who
 * wrote a sentence about their data that a parser rejected a token.
 *
 * **Two classes went away with the vocabulary (PRD-1405), and one arrived.**
 * `outside_vocabulary` and `no_threshold_present` were refusals about the *shape*
 * of the generated query — the grammar could not express a cast or a table
 * alias, and a sentence stating its own limit generated a `HAVING`. Storing SQL
 * removes the grammar, so neither can be raised any more, and copy for them
 * would be copy for something that cannot happen.
 *
 * What replaces them is `window_not_relative`: the generator pinned a date, so
 * the query would measure the window it was written in forever. That is the
 * generator's doing rather than the author's, and it is worth another draw
 * rather than a rewrite — the far side already retries it silently inside a
 * conversation, so a copy of it reaching here means the retries were used up.
 *
 * The rest are about the sentence, so they point back at it.
 */
export const REFUSAL_MESSAGES = {
  // **Kept until the backend half of PRD-1405 merges, and no longer — PRD-1421
  // owns the removal.**
  //
  // The console ships first by design — the route cannot drop the IR while this
  // is still rendering from it — so between the two merges the deployed author
  // route still parses to the vocabulary and still raises these. Removing the
  // copy with the reader would give every author who writes a cast, an alias or
  // a self-limiting sentence the generic fallback during that window, which is
  // most of them: 13 of the 15 measured questions.
  //
  // A deadline living only in a comment is a promise with no owner, which is the
  // same shape as the defect #487 fixed — declared, handled, rendered,
  // unreachable — knowingly recreated here for a good reason. PRD-1421 covers
  // both sides, since the backend leaves a matching leftover. Raised in review of
  // this branch.
  //
  // The `HAVING` redirect is deliberately *not* restored with them. It told the
  // author to move the limit out of the question, which is advice about a
  // refusal that is about to stop existing — and the step's helper text already
  // says where the limit belongs.
  outside_vocabulary: {
    title: 'This question asks for something alerts cannot express yet',
    body:
      'Alerts understand counts, totals and averages over a window, compared '
      + 'against a limit. Try stating the question that way, or simplify it.',
  },
  no_threshold_present: {
    title: 'Set the threshold below, not in the question',
    body:
      'Describe what to measure and over what period. The limit is a control on '
      + 'this step — putting it in the question is what got it refused.',
    atThreshold: true,
  },
  window_not_relative: {
    title: 'The period in this question was read as fixed dates',
    body:
      'An alert has to keep meaning the same period every time it runs. Try '
      + 'building it again, or state the period as "the last 30 days" rather '
      + 'than as dates.',
  },
  unparseable: {
    title: 'This question could not be read',
    body: 'Try rephrasing it as a single sentence about one thing to watch.',
  },
  identifier_not_permitted: {
    title: 'That data is not available to alerts',
    body:
      'Part of this question reaches data an alert is not allowed to read. '
      + 'Choose a different dataset, or ask for access.',
  },
  identifier_not_found: {
    title: 'Part of this question does not match the data',
    body:
      'Something the question names is not in the selected datasets. Check the '
      + 'dataset selection above, or rephrase using what the preview shows.',
  },
};

/**
 * A refusal rendered for the author, whatever class it carries.
 *
 * **The `HAVING` special case is gone with the vocabulary that caused it.** A
 * sentence stating its own limit used to generate SQL with a `HAVING`, which the
 * grammar refused as `outside_vocabulary`; this function detected that shape and
 * redirected the author to the threshold control. Storing SQL accepts the
 * `HAVING`, so the sentence now builds and there is nothing to redirect. The
 * threshold is still a control rather than part of the question — the step's
 * helper text says so — but that is guidance now, not a refusal.
 *
 * The fallback stays, and matters more rather than less: PRD-1401 adds
 * `query_too_expensive`, which this build has no copy for, and an author staring
 * at a blank step is worse off than one told the question was refused without
 * knowing why. "Try rephrasing it" is also not bad advice for a query too slow
 * to schedule. PRD-1355 logs the unrecognised class on the way through, so the
 * diagnostic exists.
 */
export const describeRefusal = (refusal) => {
  if (!refusal || !refusal.reason) return null;
  return REFUSAL_MESSAGES[refusal.reason] ?? {
    title: 'This question was refused',
    body: 'It could not be turned into an alert. Try rephrasing it.',
  };
};

/**
 * A stable, non-empty `alert_id` for the single comparison this wizard builds.
 *
 * `authoring.validate_spec` requires a non-empty string and refuses duplicates
 * across comparisons. It is *not* run through `check_identifier` (it is absent
 * from `_IDENTIFIER_SPEC_FIELDS`), so a hyphenated slug is fine.
 */
export const alertIdFromName = (name) => {
  const slug = String(name || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .slice(0, 48)
    // After the slice, not before: truncating "aaa… b" mid-separator would
    // otherwise leave a trailing hyphen.
    .replace(/^-+|-+$/g, '');
  return slug || 'comparison-1';
};

/**
 * How a result column is offered, and what it is called.
 *
 * The description is the column's own, carried through from the query result.
 * Where there is one it leads, because it is the sentence a person wrote about
 * that column and the name is an identifier they did not. Where there is not,
 * the name stands alone rather than being prettified — inventing a label for a
 * column would put a word in front of the author that is in neither their data
 * nor their question.
 */
export const columnLabel = (column) => {
  if (!column) return '';
  const name = typeof column === 'string' ? column : column.name;
  const description = typeof column === 'string' ? null : column.description;
  return description ? `${description} (${name})` : String(name ?? '');
};

/**
 * The result columns, normalised.
 *
 * The endpoint's contract is a list of `{name, description}`, but a list of
 * bare strings is what every other preview in this console passes around, and
 * the two meet here. Normalising rather than assuming means a column list from
 * either source renders, and a malformed entry is dropped rather than rendering
 * an option with no value behind it.
 */
export const resultColumns = (columns) => (columns ?? [])
  .map((c) => (typeof c === 'string' ? { name: c, description: null } : c))
  .filter((c) => c && c.name);

/**
 * Whether the stored query binds a window at run time.
 *
 * Mirrors `_sql_alert_from_json`, which refuses a query carrying these
 * placeholders with no `window` beside it — and refuses a `window` with no
 * placeholders to bind. The API is the authority; this only moves the refusal to
 * the step that can fix it, rather than meeting it as a 422 after steps 3 and 4
 * are filled in. Raised in review of this branch.
 */
const BINDS_A_WINDOW = /%\(window_(?:start|end)\)s/;

/**
 * A numeric form value, or null when the author has not really entered one.
 *
 * **Whitespace is the trap.** `Number('   ')` is **0**, not NaN — so a
 * threshold of spaces read as a threshold of 0, which with `>=` matches every
 * subject the alert evaluates. Accepted silently, no warning, an alert that
 * fires on everything.
 */
const numeric = (raw) => {
  if (raw == null) return null;
  const text = String(raw).trim();
  if (text === '') return null;
  const n = Number(text);
  return Number.isNaN(n) ? NaN : n;
};

/**
 * State the comparison in force, at the point of choice.
 *
 * **AC6, and the reason it is an acceptance criterion at all.** The comparators
 * are deliberately not uniform — some alerts fire on a value exactly at the
 * threshold and one is a strict `<` — so an author who assumes the wrong one
 * writes an alert that is quiet when they expect it to fire, and nothing about
 * the form tells them which they picked.
 *
 * So this names the boundary case rather than restating the operator: "at or
 * above 3" is what they chose, "a subject sitting at exactly 3 counts as
 * breached" is the thing they need to have decided.
 */
export const describeComparison = ({
  shape,
  comparator,
  threshold,
  thresholdKind,
  thresholdColumn,
  measureColumn,
  periodUnit,
  runLength,
}) => {
  if (shape === 'any_rows') {
    return 'Every row the query returns is a match. There is no threshold to breach.';
  }

  const op = COMPARATORS.find((c) => c.id === comparator);
  if (!op) return '';

  const byColumn = thresholdKind === 'column';
  // Whitespace is not a threshold — see `numeric`. A blank one means the
  // sentence cannot be completed truthfully, so none is offered.
  const value = byColumn ? null : numeric(threshold);
  if (byColumn) {
    if (!String(thresholdColumn || '').trim()) return '';
  } else if (value == null || Number.isNaN(value)) {
    return '';
  }

  const subject = measureColumn ? `a subject's ${measureColumn}` : 'a subject\'s value';
  const limit = byColumn ? `its own ${thresholdColumn}` : String(value);
  const operator = op.label.replace(/\s*\([^)]*\)/, '');
  const exactly = byColumn ? 'that value' : limit;

  const boundary = op.inclusive
    ? `A value sitting exactly on ${exactly} counts as breached.`
    : `A value sitting exactly on ${exactly} does not count as breached.`;

  if (shape === 'run_length') {
    const n = numeric(runLength);
    const unit = PERIOD_UNITS.find((u) => u.id === periodUnit);
    const run = n && !Number.isNaN(n) && unit
      ? ` A subject matches once that happens on ${n} consecutive ${unit.label.toLowerCase()}.`
      : '';
    return `Breached when ${subject} is ${operator} ${limit} in a period. ${boundary}${run}`;
  }

  return `Breached when ${subject} is ${operator} ${limit}. ${boundary}`;
};

/**
 * Whether a column the comparison names is still one the result carries.
 *
 * **This is the check the old model could not perform at all.** A typed column
 * name was validated against a regex and nothing else, so a comparison could
 * name a column the query did not return and store cleanly — the API raises
 * `the result has no column` at evaluation, which is 6am. Here the options come
 * from the result, so the only way to hold a stale one is to have picked it and
 * then changed the question. That is worth catching, because it is silent
 * otherwise: the control still shows the old value.
 *
 * Silent when the result carries no columns at all, which is a different state
 * — the query has not been run yet, and `validateAlertSpecInputs` says so once
 * rather than once per control.
 */
const staleColumn = (name, columns, what) => {
  const chosen = String(name || '').trim();
  if (!chosen) return null;
  if (columns.length === 0) return null;
  if (columns.some((c) => c.name === chosen)) return null;
  return `The query no longer returns ${chosen}, so it cannot be ${what}. Choose from the result.`;
};

/**
 * Every reason the comparison is not usable, in the author's words.
 *
 * Mirrors `ResultComparison.__post_init__` refusal for refusal. The API stays
 * the authority — this only moves the same refusal earlier, in the same spirit
 * as refusing at write time rather than at 6am.
 */
export const validateAlertSpecInputs = (formData) => {
  const problems = [];
  const alert = formData.alert ?? {};
  const columns = resultColumns(alert.resultColumns);

  /**
   * **One check where there used to be two, because there is now one source.**
   *
   * Version 2 checked a chosen column twice: against the columns the preview
   * came back with, and against the aliases the IR's `select` projected. Two
   * readings of the same fact, kept apart deliberately so that a disagreement
   * between them was caught rather than assumed away.
   *
   * Version 3 has no `select` to read — the document holds SQL, and the console
   * does not parse SQL. So the result columns are the only answer available, and
   * they are also what the API now checks against (`_check_stored_sql_resolves`
   * falls back to the stored `result_columns`). Mirroring one check is honest;
   * inventing a second from a parser here would be the console forming its own
   * opinion about a query it did not write.
   */
  const columnProblem = (name, what) => staleColumn(String(name || '').trim(), columns, what);

  // The query comes first, because every control below is populated from it.
  // Without it there is nothing to compare against and no columns to choose
  // from, so saying "choose a subject column" would be pointing at an empty
  // list.
  //
  // **This is what an alert saved before PRD-1405 hits when it is opened for
  // editing.** A version 2 document carries an IR and no SQL, so the form opens
  // with the comparison in place and asks for one rebuild before it will save —
  // the question is stored beside the alert, so that is a button press. The
  // alternative was converting the IR to SQL in the console, which would author
  // a query nobody previewed.
  if (!alert.sql) {
    problems.push('Run the question above to get a result the alert can compare.');
    return problems;
  }

  // Both directions, as the API refuses them. Neither is the author's doing —
  // they come from the two halves of the answer arriving out of step — so the
  // message says what to do rather than what they did wrong.
  if (BINDS_A_WINDOW.test(alert.sql) && !alert.window) {
    problems.push('The query measures a moving period but nothing says how long. Build it again.');
  } else if (alert.window && !BINDS_A_WINDOW.test(alert.sql)) {
    problems.push('The query carries a period it does not use. Build it again.');
  }

  if (!alert.shape) {
    problems.push('Choose what the alert does with the result.');
  }
  if (!String(alert.subjectColumn || '').trim()) {
    problems.push('Choose the column that identifies the subject.');
  } else {
    const problem = columnProblem(alert.subjectColumn, 'the subject');
    if (problem) problems.push(problem);
  }

  const shape = COMPARISON_SHAPES.find((s) => s.id === alert.shape);
  if (!shape) return problems;

  // Nothing further for an anti-join: the absence *is* the condition, and the
  // API refuses every field a threshold would need.
  if (shape.id === 'any_rows') return problems;

  if (!String(alert.measureColumn || '').trim()) {
    problems.push('Choose the column holding the value to compare.');
  } else {
    const problem = columnProblem(alert.measureColumn, 'measured');
    if (problem) problems.push(problem);
  }

  // `(threshold is None) == (threshold_column is None)` on the API side: both
  // or neither is the refusal. The form models it as a choice so "both" is
  // unreachable, which leaves "neither" as the one an author can still reach.
  if (alert.thresholdKind === 'column') {
    if (!String(alert.thresholdColumn || '').trim()) {
      problems.push('Choose the column holding each subject\'s own threshold.');
    } else {
      const problem = columnProblem(alert.thresholdColumn, 'the threshold');
      if (problem) problems.push(problem);
    }
  } else {
    const threshold = numeric(alert.threshold);
    if (threshold == null) {
      problems.push('Set the threshold. A blank threshold is not zero.');
    } else if (Number.isNaN(threshold) || !Number.isFinite(threshold)) {
      // An infinite threshold makes every comparison false, so the alert
      // evaluates forever and never fires — and nothing looks wrong.
      problems.push('The threshold must be a finite number.');
    }
  }

  if (!alert.comparator) {
    problems.push('Choose how the value is compared against the threshold.');
  }

  if (shape.id === 'run_length') {
    if (!String(alert.periodColumn || '').trim()) {
      problems.push('Choose the column that groups the result into periods.');
    } else {
      const problem = columnProblem(alert.periodColumn, 'the period');
      if (problem) problems.push(problem);
    }
    if (!alert.periodUnit) {
      // Without it "consecutive" can only mean consecutive rows, and the
      // period that should break a run is precisely the one the result omits.
      problems.push('Choose how long one period is, or a gap in the data reads as a run.');
    }
    const run = numeric(alert.runLength);
    // `Number.isSafeInteger`, not `isInteger`: 1e21 is integral but not exactly
    // representable, and passed the looser check.
    if (run == null || !Number.isSafeInteger(run) || run < 2) {
      // A run of one is just a threshold breach, which the threshold shape
      // already says more plainly. The API's floor is 1; this is deliberately
      // stricter, because a run of 1 offered here is a second way to spell a
      // shape that already exists.
      problems.push('A run needs a whole number of periods, at least 2.');
    }
  }

  return problems;
};

/**
 * The stored document: the query, and the comparison over its result.
 *
 * The two travel together because neither is meaningful alone — one says what
 * to read, the other what to do with it. Holding them apart is the
 * two-definitions problem this epic exists to remove.
 *
 * Returns null when the comparison is not buildable, so a caller cannot send a
 * body the API would refuse.
 *
 * **Fields the shape does not read are omitted, not sent empty.** The API
 * refuses a `run_length` on a threshold comparison rather than ignoring it, so
 * a form that carried a stale value from a shape the author switched away from
 * would be refused on save. Building from the shape rather than from the form
 * state is what makes switching shapes safe.
 */
export const buildAlertSpec = (formData) => {
  if (validateAlertSpecInputs(formData).length > 0) return null;

  const alert = formData.alert ?? {};
  const shape = COMPARISON_SHAPES.find((s) => s.id === alert.shape);
  if (!shape) return null;

  const comparison = {
    // **Preserved when editing.** Regenerating it renamed a stored comparison —
    // ALERT_2 became a slug of the automation's name — and anything keyed on
    // `alert_id` stopped matching. A new alert gets a slug; an existing one
    // keeps the id it was stored with.
    alert_id: String(alert.alertId || '').trim() || alertIdFromName(formData.automationName),
    subject_column: String(alert.subjectColumn).trim(),
    shape: shape.id,
  };

  if (shape.id !== 'any_rows') {
    comparison.measure_column = String(alert.measureColumn).trim();
    // Omitted for any_rows, where the API refuses a comparator outright — the
    // absence is the condition and there is nothing to compare.
    comparison.comparator = alert.comparator;

    if (alert.thresholdKind === 'column') {
      comparison.threshold_column = String(alert.thresholdColumn).trim();
    } else {
      comparison.threshold = numeric(alert.threshold);
    }
  }

  if (shape.id === 'run_length') {
    comparison.period_column = String(alert.periodColumn).trim();
    comparison.period_unit = alert.periodUnit;
    comparison.run_length = numeric(alert.runLength);
  }

  // `round_to` is deliberately never written. It is a parity shim for alerts
  // migrated from the old model — PRD-1320 sets it on exactly the shapes that
  // carried the old rounding default, so a migrated alert evaluates identically
  // — and the API's comment says in as many words that new alerts should leave
  // it alone. A wizard that offered it would be offering a compatibility
  // artefact as a setting.

  // **Three fields, and each one breaks something different by its absence.**
  //
  //  - `sql` is the query, its window held as `%(window_start)s` /
  //    `%(window_end)s` so it keeps meaning a moving period.
  //  - `window` is what those placeholders bind to. Without it **every run
  //    fails** on a missing value, and the staleness probe loses its clock;
  //    `_sql_alert_from_json` refuses the pairing rather than storing it, in
  //    both directions.
  //  - `result_columns` is what the comparison is checked against. Without it
  //    that check returns early, and an alert whose comparison names a column
  //    the query never projects saves and activates cleanly — then raises at
  //    6am.
  //
  // `window` is omitted rather than sent as null when the query has no date
  // filter at all, which is a real and common shape: 11 of 15 measured dashboard
  // questions carried no window. Sending null would be indistinguishable from a
  // window the console failed to carry.
  const spec = {
    version: ALERT_DOC_VERSION,
    // **The query is no longer sent** (PRD-1438). It is referenced by
    // `alertQueryDraftId` on the request beside this spec, and the server
    // fills it in before anything validates or stores it. App Gateway's WAF
    // reads a request body carrying SQL as injection and answers 403 before
    // it reaches resapi, which made alert authoring unusable on dev.
    //
    // Sending both is refused server-side rather than reconciled, so this is
    // a removal and not a second channel: `sql` and the reference name one
    // thing, and two sources for it is the problem this epic removed once
    // already.
    //
    // The key is absent, not undefined: `'sql' in spec` is how a stored document
    // is told apart from a version 1 one, and a key present-but-undefined would
    // answer that question wrongly while serialising identically.
    result_columns: resultColumns(alert.resultColumns).map((c) => c.name),
    comparison,
  };
  if (alert.window) spec.window = alert.window;
  return spec;
};

/**
 * The share the guard is configured with, as the API wants it.
 *
 * The column is `Numeric(6, 5)` and the endpoint bounds the value to `(0, 1]`,
 * so a percentage typed as `50` is refused — it has to arrive as `0.5`. The
 * form takes a percentage because that is how the number is discussed.
 */
export const percentToShare = (percent) => {
  const n = percent === '' || percent == null ? null : Number(percent);
  if (n == null || Number.isNaN(n) || n <= 0 || n > 100) return null;
  // Five decimal places is the column's limit; round rather than let the
  // database round it into a CHECK violation. A share small enough to round to
  // zero is refused rather than sent — the endpoint bounds the value above 0,
  // so a rounded 0 is a 422 the wizard thought it had validated.
  const rounded = Math.round((n / 100) * 100000) / 100000;
  return rounded > 0 ? rounded : null;
};
